import { I18n } from '@affine/i18n';
import type {
  DatabaseAppUsage,
  DatabaseAppUsagePort,
  DatabaseAppUsageRead,
} from '@blocksuite/affine/blocks/database';
import type { FrameworkProvider } from '@toeverything/infra';

import {
  readSystemOnline,
  subscribeOnlineStatus,
} from '../../../components/hooks/use-system-online';
import { PublicUserService } from '../../cloud';
import { WorkspaceService } from '../../workspace';
import { requestAppResourceJson } from './app-resource-http';
import { appResourceLifecycleImpactBaseUrl } from './app-resource-lifecycle';
import type { AppResourceKind } from './app-resource-preferences';

/** One app reading a database, as `database-usages` answers it. */
export interface AppResourceDatabaseUsage {
  dataSourceLabel: string;
  databaseId: string;
  hostDocId: string;
  readIdentityUserId: string | null;
  resourceId: string;
  resourceKind: AppResourceKind;
  stage: 'published' | 'draft';
  state: 'active' | 'trashed';
  title: string;
}

interface DatabaseUsagesResponse {
  usages: AppResourceDatabaseUsage[];
}

type Usage = Omit<DatabaseAppUsage, 'ownerName'> & {
  ownerId: string | null;
};

/** One entry per app: published wins over draft, in use over trashed. */
function usagesPerApp(usages: readonly AppResourceDatabaseUsage[]): Usage[] {
  const apps = new Map<string, Usage>();
  for (const usage of usages) {
    const key = `${usage.resourceKind}:${usage.resourceId}`;
    const known = apps.get(key);
    apps.set(key, {
      resourceId: usage.resourceId,
      title: usage.title,
      ownerId: usage.readIdentityUserId,
      stage: known?.stage === 'published' ? 'published' : usage.stage,
      state: known?.state === 'active' ? 'active' : usage.state,
    });
  }
  return [...apps.values()];
}

/**
 * Reports the usages at once, so a database in use never looks free while
 * names load, and again whenever a responsible person resolves. A name still
 * loading reads "…", an unknown or removed user "Unknown User".
 */
function followOwnerNames(
  users: PublicUserService,
  usages: readonly Usage[],
  onChange: (read: DatabaseAppUsageRead) => void
): () => void {
  const ids = [
    ...new Set(usages.flatMap(usage => (usage.ownerId ? [usage.ownerId] : []))),
  ];
  let subscribed = false;
  const report = () => {
    if (!subscribed) return;
    const nameOf = (id: string | null) => {
      if (!id) return I18n['Unknown User']();
      const user = users.publicUser$(id).value;
      if (user && !user.removed && user.name) return user.name;
      return user || users.error$(id).value ? I18n['Unknown User']() : '…';
    };
    onChange({
      status: 'ready',
      usages: usages.map(({ ownerId, ...usage }) => ({
        ...usage,
        ownerName: nameOf(ownerId),
      })),
    });
  };
  const subscriptions = ids.flatMap(id => {
    if (!users.publicUser$(id).value) users.revalidate(id);
    return [
      users.publicUser$(id).subscribe(report),
      users.error$(id).subscribe(report),
    ];
  });
  subscribed = true;
  report();
  return () => {
    for (const subscription of subscriptions) subscription.unsubscribe();
  };
}

/**
 * Which apps read a database of this workspace. Offline or after a failed
 * read the answer is unknown, never "no apps"; back online it reads again.
 * Local workspaces have no apps and offer no port.
 */
export function createDatabaseAppUsagePort(
  framework: FrameworkProvider
): DatabaseAppUsagePort | undefined {
  const workspace = framework.getOptional(WorkspaceService)?.workspace;
  const users = framework.getOptional(PublicUserService);
  if (!workspace || workspace.flavour === 'local' || !users) return undefined;
  const workspaceId = workspace.id;
  return {
    watch(databaseId, onChange) {
      let stopped = false;
      let generation = 0;
      let stopNames: (() => void) | null = null;
      const read = async () => {
        const current = ++generation;
        stopNames?.();
        stopNames = null;
        if (!readSystemOnline()) {
          onChange({ status: 'unknown' });
          return;
        }
        let usages: AppResourceDatabaseUsage[];
        try {
          const query = new URLSearchParams({ databaseId });
          usages = (
            await requestAppResourceJson<DatabaseUsagesResponse>(
              `${appResourceLifecycleImpactBaseUrl(
                workspaceId
              )}/database-usages?${query.toString()}`
            )
          ).usages;
        } catch {
          if (!stopped && current === generation) {
            onChange({ status: 'unknown' });
          }
          return;
        }
        if (stopped || current !== generation) return;
        stopNames = followOwnerNames(users, usagesPerApp(usages), onChange);
      };
      const stopOnline = subscribeOnlineStatus(() => {
        void read();
      });
      void read();
      return () => {
        stopped = true;
        generation++;
        stopOnline();
        stopNames?.();
        stopNames = null;
      };
    },
  };
}

/**
 * The first answer of the port for one database, then no longer watched.
 * Without a port the usage is unknown.
 */
export function readDatabaseAppUsageOnce(
  port: DatabaseAppUsagePort | undefined,
  databaseId: string
): Promise<DatabaseAppUsageRead> {
  if (!port) return Promise.resolve({ status: 'unknown' });
  return new Promise(resolve => {
    let answered = false;
    let stop: (() => void) | null = null;
    stop = port.watch(databaseId, read => {
      if (answered) return;
      answered = true;
      resolve(read);
      stop?.();
    });
    // The port may answer before watch returns its stop function.
    if (answered) stop();
  });
}
